import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type ContactFormProps = {
  onSuccess?: () => void;
  hideFields?: string[];
  pageName?: string;
};

const ContactForm: React.FC<ContactFormProps> = ({ onSuccess, hideFields = [], pageName }) => {
  const { t } = useTranslation();
  const isVisible = (field: string) => !hideFields.includes(field);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submitted, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = formRef.current;
    if (form) {
      const formData = new FormData(form);
      fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (response.ok) {
            setSubmitted(true);
            form.reset();
            if (onSuccess) onSuccess(); // ⬅️ BEKRÄFTELSE-HANTERING
          }
        })
        .catch(() => {
          // Hantera ev. fel här
        });
    }
  };

  return (
    <Box
      component="form"
      ref={formRef}
      action="https://formspree.io/f/xeogggqj"
      method="POST"
      onSubmit={handleSubmit}
      sx={{ maxWidth: 800, margin: 'auto', p: 2 }}
    >
      <Typography variant="h4" gutterBottom>{t('contactform.title')}</Typography>
      <Typography variant="body1" gutterBottom>{t('contactform.subtitle')}</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField name="orgNumber" label={t('contactform.orgNumber')} fullWidth required />
        </Grid>

        <Grid item xs={12}>
          <TextField name="contactName" label={t('contactform.contactName')} fullWidth required />
        </Grid>

        <Grid item xs={12}>
          <TextField name="contactEmail" label={t('contactform.contactEmail')} type="email" fullWidth required />
        </Grid>

        <Grid item xs={12}>
          <TextField name="businessType" label={t('contactform.businessType')} fullWidth required />
        </Grid>

        {isVisible('employees') && (
          <Grid item xs={12}>
            <TextField name="employees" label={t('contactform.employees')} fullWidth />
          </Grid>
        )}

        {isVisible('businessStatus') && (
          <Grid item xs={12}>
            <TextField name="businessStatus" label={t('contactform.businessStatus')} fullWidth />
          </Grid>
        )}

        {isVisible('previousSystem') && (
          <Grid item xs={12}>
            <TextField name="previousSystem" label={t('contactform.previousSystem')} fullWidth />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField name="city" label={t('contactform.city')} fullWidth />
        </Grid>

        {isVisible('services') && (
          <Grid item xs={12}>
            <TextField name="services" label={t('contactform.services')} fullWidth />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField name="deliveryMethod" label={t('contactform.deliveryMethod')} fullWidth />
        </Grid>

        {isVisible('startDate') && (
          <Grid item xs={12}>
            <TextField
              name="startDate"
              label={t('contactform.startDate')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField name="message" label={t('contactform.message')} fullWidth multiline rows={4} />
        </Grid>

        {isVisible('requestedServices') && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: '#c6a04c', mb: 1 }}>
              {t('contactform.helpTitle')}
            </Typography>
            <FormGroup row sx={{ flexWrap: 'wrap', gap: 2 }}>
              <FormControlLabel
                control={<Checkbox name="requestedServices" value="Ekonomitjänster" />}
                label={t('contactform.helpOptions.accounting')}
              />
              <FormControlLabel
                control={<Checkbox name="requestedServices" value="Webbdesign" />}
                label={t('contactform.helpOptions.web')}
              />
              <FormControlLabel
                control={<Checkbox name="requestedServices" value="Juridik & Översättning" />}
                label={t('contactform.helpOptions.legal')}
              />
              <FormControlLabel
                control={<Checkbox name="requestedServices" value="Rådgivning" />}
                label={t('contactform.helpOptions.advice')}
              />
            </FormGroup>
          </Grid>

        )}

        {pageName && (
          <input type="hidden" name="page" value={pageName} />
        )}

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: '#c6a04c',
              color: '#000',
              '&:hover': { backgroundColor: '#d1ae5a' },
            }}
          >
            {t('contactform.submit')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactForm;
